import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Typography, Tabs, Tab, Paper, Button, TextField, InputAdornment, Grid, Chip, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Tooltip, FormControl, InputLabel, Select, MenuItem, Divider, Snackbar, Alert, CircularProgress, List, ListItem, ListItemText, Checkbox, TableSortLabel, Modal } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import HistoryIcon from '@mui/icons-material/History';
import ArchiveIcon from '@mui/icons-material/Archive';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RoomFormModal from '../../components/RoomFormModal';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import RoomOOOModal from '../../components/RoomOOOModal';
import RoomQRCodeModal from '../../components/RoomQRCodeModal';
import BulkQRPrintModal from '../../components/BulkQRPrintModal';
import QrCodeIcon from '@mui/icons-material/QrCode';
import PrintIcon from '@mui/icons-material/Print';
import RoomTypeSetup from './RoomTypeSetup';
import FloorSetup from './FloorSetup';
import WingSetup from './WingSetup';
import AmenitySetup from './AmenitySetup';
import { RoomService } from '../../api/services/roomService';
import { StaffService } from '../../api/services/staffService';
import BuildIcon from '@mui/icons-material/Build';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MoreTimeIcon from '@mui/icons-material/MoreTime';
import CloseIcon from '@mui/icons-material/Close';
import ReplayIcon from '@mui/icons-material/Replay';
import { useCurrency, formatCurrency } from '../../utils/currency';
import { standardFilterContainerSx, standardSearchInputSx, standardFilterSelectSx, standardResetButtonSx } from '../../components/common/tableStyles';

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
}

export default function RoomManagement() {
    const { currency } = useCurrency();
    const navigate = useNavigate();
    const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
    const tenantId = authData.tenantId || '1';
    const { tenants } = useSelector((state) => state.auth || { tenants: [] });
    const tenant = tenants.find(t => String(t.tenantId) === String(tenantId) || String(t.id) === String(tenantId) || String(t._id) === String(tenantId));
    const hotelStatus = tenant?.status || 'Active';
    const isGeometryLocked = hotelStatus === 'Active';
    const [tabValue, setTabValue] = useState(0);
    const [selectedRooms, setSelectedRooms] = useState([]);
    const [bulkStatus, setBulkStatus] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [roomModal, setRoomModal] = useState({ open: false, data: null });
    const [oooModal, setOooModal] = useState({ open: false, data: null });
    const [qrModal, setQrModal] = useState({ open: false, data: null });
    const [bulkQrModal, setBulkQrModal] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, roomId: null });
    const [archiveDialog, setArchiveDialog] = useState({ open: false, roomId: null });
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
    
    // Filters and Sorting
    const [floorFilter, setFloorFilter] = useState('All');
    const [wingFilter, setWingFilter] = useState('All');
    const [roomTypeFilter, setRoomTypeFilter] = useState('All');
    const [sortBy, setSortBy] = useState('roomNumber');
    const [sortOrder, setSortOrder] = useState('asc');

    // Lookup data
    const [lookupData, setLookupData] = useState({ floors: [], wings: [], roomTypes: [] });
    const [staffList, setStaffList] = useState([]);
    
    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    const dispatch = useDispatch();
    const roomsList = useSelector(state => state.rooms.list) || [];
    const totalRooms = useSelector(state => state.rooms.total) || 0;
    const isLoading = useSelector(state => state.rooms.isLoading);
    const storeError = useSelector(state => state.rooms.error);

    useEffect(() => {
        if (storeError) {
            setNotification({ open: true, message: storeError, severity: 'error' });
            dispatch.rooms.setError(null);
        }
    }, [storeError, dispatch]);

    useEffect(() => {
        const fetchLookupData = async () => {
            try {
                const [floorsRes, wingsRes, typesRes, staffRes] = await Promise.all([
                    RoomService.getFloors(),
                    RoomService.getWings(),
                    RoomService.getRoomTypes(),
                    StaffService.getStaff({ noPagination: true }).catch(() => [])
                ]);
                setLookupData({
                    floors: floorsRes.data || floorsRes || [],
                    wings: wingsRes.data || wingsRes || [],
                    roomTypes: typesRes.data || typesRes || []
                });
                const sList = Array.isArray(staffRes)
                    ? staffRes
                    : (Array.isArray(staffRes?.data)
                        ? staffRes.data
                        : (Array.isArray(staffRes?.staff)
                            ? staffRes.staff
                            : (Array.isArray(staffRes?.data?.staff)
                                ? staffRes.data.staff
                                : (Array.isArray(staffRes?.staffMembers)
                                    ? staffRes.staffMembers
                                    : []))));
                setStaffList(sList);
            } catch (err) {
                console.error("Failed to fetch lookup data", err);
            }
        };
        fetchLookupData();
    }, []);

    const refreshRooms = useCallback(() => {
        return dispatch.rooms.fetchRoomsAsync({
            search: searchTerm,
            page: page,
            limit: rowsPerPage,
            status: statusFilter,
            floorId: floorFilter !== 'All' ? floorFilter : undefined,
            wingId: wingFilter !== 'All' ? wingFilter : undefined,
            roomTypeId: roomTypeFilter !== 'All' ? roomTypeFilter : undefined,
            sortBy: sortBy,
            sortOrder: sortOrder
        });
    }, [dispatch, searchTerm, page, rowsPerPage, statusFilter, floorFilter, wingFilter, roomTypeFilter, sortBy, sortOrder]);

    useEffect(() => {
        refreshRooms();
    }, [refreshRooms]);

    // Reset pagination when filters or sort change
    useEffect(() => {
        setPage(0);
    }, [searchTerm, statusFilter, floorFilter, wingFilter, roomTypeFilter, sortBy, sortOrder]);

    const handleSort = (field) => {
        const isAsc = sortBy === field && sortOrder === 'asc';
        setSortOrder(isAsc ? 'desc' : 'asc');
        setSortBy(field);
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleSaveRoom = async (roomData) => {
        try {
            let payloadData = roomData;
            
            if (roomModal.data) {
                // Merge but make sure we don't bring back populated objects from roomModal.data that we already cleaned
                payloadData = { ...roomModal.data, ...roomData };
                delete payloadData.createdAt;
                delete payloadData.type;
                delete payloadData.floor;
                delete payloadData.wing;
                delete payloadData.roomType;
            }
            
            if (roomModal.data) {
                await dispatch.rooms.saveRoomAsync(payloadData);
                setNotification({ open: true, message: 'Room updated successfully!', severity: 'success' });
            } else {
                await dispatch.rooms.saveRoomAsync(payloadData);
                setNotification({ open: true, message: 'Room created successfully!', severity: 'success' });
            }
            await refreshRooms();
        } catch (error) {
            if (error.response?.status === 403 || error.response?.status === 405) {
                setNotification({ open: true, message: 'Action not allowed while the hotel is suspended.', severity: 'error' });
            } else {
                setNotification({ open: true, message: error.message || 'Failed to save room', severity: 'error' });
            }
        }
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            setSelectedRooms(roomsList.map(room => room.id));
        } else {
            setSelectedRooms([]);
        }
    };

    const handleSelectRoom = (id) => {
        setSelectedRooms(prev => 
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    const handleBulkStatusUpdate = async () => {
        if (!bulkStatus || selectedRooms.length === 0) return;
        
        if (bulkStatus === 'OOO') {
            setOooModal({ open: true, data: selectedRooms, isExtend: false, isBulk: true });
            return;
        }
        
        try {
            await dispatch.rooms.bulkUpdateStatusAsync({
                roomIds: selectedRooms.map(id => String(id)),
                status: bulkStatus
            });
            setNotification({ open: true, message: 'Rooms updated successfully!', severity: 'success' });
            setSelectedRooms([]);
            setBulkStatus('');
            await refreshRooms();
        } catch (error) {
            setNotification({ open: true, message: error.message || 'Bulk update failed', severity: 'error' });
        }
    };

    const handleArchiveClick = (id) => {
        setArchiveDialog({ open: true, roomId: id });
    };

    const confirmArchive = async () => {
        if (archiveDialog.roomId) {
            try {
                await dispatch.rooms.archiveRoomAsync(archiveDialog.roomId);
                setNotification({ open: true, message: 'Room archived successfully!', severity: 'success' });
                await refreshRooms();
            } catch (error) {
                setNotification({ open: true, message: error.message || 'Failed to archive room', severity: 'error' });
            }
        }
        setArchiveDialog({ open: false, roomId: null });
    };

    const handleDeleteClick = (id) => {
        setDeleteDialog({ open: true, roomId: id });
    };

    const confirmDelete = async () => {
        if (deleteDialog.roomId) {
            try {
                await dispatch.rooms.deleteRoomAsync(deleteDialog.roomId);
                setNotification({ open: true, message: 'Room deleted successfully!', severity: 'success' });
                await refreshRooms();
            } catch (error) {
                setNotification({ open: true, message: error.message || 'Failed to delete room', severity: 'error' });
            }
        }
        setDeleteDialog({ open: false, roomId: null });
    };

    const handleOOOConfirm = async (id, payload, isExtend, isBulk = false) => {
        try {
            if (isBulk) {
                const roomIds = oooModal.data; // array of IDs
                const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
                const tenantId = authData.tenantId || '1';
                
                // Use the bulk status API with OOO payload details
                await dispatch.rooms.bulkUpdateStatusAsync({
                    roomIds: roomIds.map(roomId => String(roomId)),
                    status: 'Out of Order',
                    ...payload
                });
                
                setNotification({ open: true, message: `Successfully set ${roomIds.length} rooms to Out-of-Order`, severity: 'success' });
                setSelectedRooms([]);
                setBulkStatus('');
            } else if (isExtend) {
                const room = oooModal.data;
                const oooId = room.oooId || (room.activeOOO && room.activeOOO.id) || (room.activeOOORecord && room.activeOOORecord.id) || (room.outOfOrders && room.outOfOrders.find(o => o.status === 'Active')?.id) || 'current';
                await RoomService.extendOOO(id, oooId, payload);
                setNotification({ open: true, message: 'Room Out-of-Order period extended successfully', severity: 'success' });
            } else {
                const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
                const tenantId = authData.tenantId || '1';
                await RoomService.setOOO(id, payload, tenantId);
                setNotification({ open: true, message: 'Room set to Out-of-Order successfully', severity: 'success' });
            }
            // Refresh list
            await refreshRooms();
        } catch (err) {
            console.error("Set out-of-order failed", err);
            setNotification({ open: true, message: err.message || 'Failed to set Out-of-Order', severity: 'error' });
        }
    };

    const handleResolveOOOClick = async (room) => {
        console.log(room,"room")
        // Find the oooId or fallback to 'current' so the network request can fire
        const oooId = room.oooId || (room.activeOOO && room.activeOOO.id) || (room.activeOOORecord && room.activeOOORecord.id) || (room.outOfOrders && room.outOfOrders.find(o => o.status === 'Active')?.id) || 'current';

        try {
            await RoomService.resolveOOO(room.id, oooId);
            setNotification({ open: true, message: 'Room Out-of-Order resolved successfully', severity: 'success' });
            // Refresh list
            await refreshRooms();
        } catch (err) {
            console.error("Resolve OOO failed", err);
            setNotification({ open: true, message: err.message || 'Failed to resolve Out-of-Order', severity: 'error' });
        }
    };

    const getOOOStaffName = (room) => {
        if (!room) return null;

        // 1. Check direct staff properties on room object
        if (typeof room.assignedStaff === 'string' && room.assignedStaff) return room.assignedStaff;
        if (room?.assignedStaff?.fullName) return room.assignedStaff.fullName;
        if (room?.assignedStaff?.name) return room.assignedStaff.name;
        if (room?.assignedStaffName) return room.assignedStaffName;
        if (room?.staffName) return room.staffName;

        // 2. Check outOfOrders array - find active records or records with assigned staff
        if (room.outOfOrders && Array.isArray(room.outOfOrders) && room.outOfOrders.length > 0) {
            const activeList = room.outOfOrders.filter(o => o.status === 'Active');
            // Prioritize an active record that has assignedStaff object/fullName/name/id
            const activeWithStaffObj = activeList.slice().reverse().find(o => o.assignedStaff?.fullName || o.assignedStaff?.name || typeof o.assignedStaff === 'string' || o.assignedStaffName);
            const activeWithStaffId = activeList.slice().reverse().find(o => o.assignedStaffId || o.staffId);
            const targetOOO = activeWithStaffObj || activeWithStaffId || activeList[activeList.length - 1] || room.outOfOrders[room.outOfOrders.length - 1];

            if (targetOOO) {
                if (typeof targetOOO.assignedStaff === 'string' && targetOOO.assignedStaff) return targetOOO.assignedStaff;
                if (targetOOO.assignedStaff?.fullName) return targetOOO.assignedStaff.fullName;
                if (targetOOO.assignedStaff?.name) return targetOOO.assignedStaff.name;
                if (targetOOO.assignedStaffName) return targetOOO.assignedStaffName;
                if (targetOOO.staffName) return targetOOO.staffName;

                const staffId = targetOOO.assignedStaffId || targetOOO.staffId;
                if (staffId && staffList && staffList.length > 0) {
                    const matched = staffList.find(s => String(s.id || s._id) === String(staffId));
                    if (matched) {
                        return matched.fullName || matched.name || `${matched.firstName || ''} ${matched.lastName || ''}`.trim() || matched.email;
                    }
                }
            }
        }

        // 3. Fallback to activeOOO or activeOOORecord
        const fallbackOOO = room.activeOOO || room.activeOOORecord;
        if (fallbackOOO) {
            if (typeof fallbackOOO.assignedStaff === 'string' && fallbackOOO.assignedStaff) return fallbackOOO.assignedStaff;
            if (fallbackOOO.assignedStaff?.fullName) return fallbackOOO.assignedStaff.fullName;
            if (fallbackOOO.assignedStaff?.name) return fallbackOOO.assignedStaff.name;
            if (fallbackOOO.assignedStaffName) return fallbackOOO.assignedStaffName;
            if (fallbackOOO.staffName) return fallbackOOO.staffName;

            const staffId = fallbackOOO.assignedStaffId || fallbackOOO.staffId;
            if (staffId && staffList && staffList.length > 0) {
                const matched = staffList.find(s => String(s.id || s._id) === String(staffId));
                if (matched) {
                    return matched.fullName || matched.name || `${matched.firstName || ''} ${matched.lastName || ''}`.trim() || matched.email;
                }
            }
        }

        // 4. Fallback lookup for room.assignedStaffId
        if (room.assignedStaffId && staffList && staffList.length > 0) {
            const matched = staffList.find(s => String(s.id || s._id) === String(room.assignedStaffId));
            if (matched) {
                return matched.fullName || matched.name || `${matched.firstName || ''} ${matched.lastName || ''}`.trim() || matched.email;
            }
        }

        return null;
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Clean': return 'success';
            case 'Occupied': return 'primary';
            case 'Cleaning': return 'info';
            case 'Repair': return 'warning';
            case 'OOO':
            case 'Out of Order': return 'error';
            default: return 'default';
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h4" fontWeight="bold">Room & Property Setup</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button variant="outlined" startIcon={<PrintIcon sx={{ fontSize: 18 }} />} onClick={() => setBulkQrModal(true)}>
                        Print All QRs
                    </Button>
                    <Tooltip title={isGeometryLocked ? "Structural additions are locked while property is Active" : ""}>
                        <span>
                            <Button variant="contained" startIcon={<AddIcon sx={{ fontSize: 18 }} />} onClick={() => setRoomModal({ open: true, data: null })} disabled={isGeometryLocked}>
                                Create Room
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: '#eaecf0', mb: 3 }}>
                <Tabs 
                    value={tabValue} 
                    onChange={handleTabChange} 
                    variant="scrollable" 
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                >
                    <Tab label="Rooms List" />
                    <Tab label="Room Types" />
                    <Tab label="Structure" />
                    <Tab label="Amenities" />
                </Tabs>
            </Box>

            <Box>
                    {/* Rooms List */}
                    <TabPanel value={tabValue} index={0}>
                        <Box sx={standardFilterContainerSx}>
                            <TextField
                                size="small"
                                placeholder="Search room number..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                sx={standardSearchInputSx}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: '#98a0aa', fontSize: 18 }} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                                <FormControl size="small" sx={{ minWidth: 140 }}>
                                    <Select value={statusFilter} displayEmpty onChange={(e) => setStatusFilter(e.target.value)} sx={standardFilterSelectSx}>
                                        <MenuItem value="All">All Status</MenuItem>
                                        <MenuItem value="Clean">Clean</MenuItem>
                                        <MenuItem value="Occupied">Occupied</MenuItem>
                                        <MenuItem value="Cleaning">Cleaning</MenuItem>
                                        <MenuItem value="Repair">Repair</MenuItem>
                                        <MenuItem value="OOO">Out of Order</MenuItem>
                                        <MenuItem value="Archive">Archive</MenuItem>
                                    </Select>
                                </FormControl>
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                    <Select value={floorFilter} displayEmpty onChange={(e) => setFloorFilter(e.target.value)} sx={standardFilterSelectSx}>
                                        <MenuItem value="All">All Floors</MenuItem>
                                        {lookupData.floors.map(f => (
                                            <MenuItem key={f.id} value={f.id}>{f.label || `Floor ${f.number}`}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                    <Select value={wingFilter} displayEmpty onChange={(e) => setWingFilter(e.target.value)} sx={standardFilterSelectSx}>
                                        <MenuItem value="All">All Wings</MenuItem>
                                        {lookupData.wings.map(w => (
                                            <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                    <Select value={roomTypeFilter} displayEmpty onChange={(e) => setRoomTypeFilter(e.target.value)} sx={standardFilterSelectSx}>
                                        <MenuItem value="All">All Types</MenuItem>
                                        {lookupData.roomTypes.map(t => (
                                            <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {(searchTerm !== '' || statusFilter !== 'All' || floorFilter !== 'All' || wingFilter !== 'All' || roomTypeFilter !== 'All') && (
                                    <Button variant="outlined" startIcon={<ReplayIcon sx={{ fontSize: 16 }} />} sx={standardResetButtonSx} onClick={() => { setSearchTerm(''); setStatusFilter('All'); setFloorFilter('All'); setWingFilter('All'); setRoomTypeFilter('All'); }}>Reset</Button>
                                )}
                            </Box>
                        </Box>

                            {selectedRooms.length > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'action.hover', px: 2, py: 0.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', ml: 'auto' }}>
                                    <Typography variant="body2" fontWeight="bold" sx={{ whiteSpace: 'nowrap' }}>{selectedRooms.length} selected</Typography>
                                    <FormControl size="small" sx={{ minWidth: 150 }}>
                                        <Select
                                            value={bulkStatus}
                                            onChange={(e) => setBulkStatus(e.target.value)}
                                            displayEmpty
                                        >
                                            <MenuItem value="" disabled>Bulk Status</MenuItem>
                                            <MenuItem value="Available">Available</MenuItem>
                                            <MenuItem value="Occupied">Occupied</MenuItem>
                                            <MenuItem value="Cleaning">Cleaning</MenuItem>
                                            <MenuItem value="Repair">Repair</MenuItem>
                                            <MenuItem value="OOO">Out of Order</MenuItem>
                                            <MenuItem value="Archive">Archive</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <Button variant="contained" size="small" onClick={handleBulkStatusUpdate} disabled={!bulkStatus}>Apply</Button>
                                </Box>
                            )}

                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                indeterminate={selectedRooms.length > 0 && selectedRooms.length < roomsList.length}
                                                checked={roomsList.length > 0 && selectedRooms.length === roomsList.length}
                                                onChange={handleSelectAll}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={sortBy === 'roomNumber'}
                                                direction={sortBy === 'roomNumber' ? sortOrder : 'asc'}
                                                onClick={() => handleSort('roomNumber')}
                                            >
                                                Room #
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={sortBy === 'type'}
                                                direction={sortBy === 'type' ? sortOrder : 'asc'}
                                                onClick={() => handleSort('type')}
                                            >
                                                Type
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={sortBy === 'floor'}
                                                direction={sortBy === 'floor' ? sortOrder : 'asc'}
                                                onClick={() => handleSort('floor')}
                                            >
                                                Floor / Wing
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>Occupancy</TableCell>
                                        <TableCell>Base Rate</TableCell>
                                        <TableCell>Features</TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={sortBy === 'status'}
                                                direction={sortBy === 'status' ? sortOrder : 'asc'}
                                                onClick={() => handleSort('status')}
                                            >
                                                Status
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {isLoading ? (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell>
                                        </TableRow>
                                    ) : roomsList.length > 0 ? (
                                        roomsList.map((room) => (
                                            <TableRow key={room.id} hover selected={selectedRooms.includes(room.id)}>
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={selectedRooms.includes(room.id)}
                                                        onChange={() => handleSelectRoom(room.id)}
                                                    />
                                                </TableCell>
                                                <TableCell 
                                                    sx={{ 
                                                        fontWeight: 'bold', 
                                                        color: 'primary.main', 
                                                        cursor: 'pointer',
                                                        '&:hover': { textDecoration: 'underline' }
                                                    }}
                                                    onClick={() => navigate(`/room-management/${room.id}`)}
                                                >
                                                    {room.roomNumber || room.number}
                                                </TableCell>
                                                <TableCell>{room.roomType && typeof room.roomType === 'object' ? room.roomType.name : (room.type && typeof room.type === 'object' ? (room.type.name || 'N/A') : (room.type || 'N/A'))}</TableCell>
                                                <TableCell>
                                                    {room.floor && typeof room.floor === 'object' ? (room.floor.label || `Floor ${room.floor.number}`) : (room.floor || 'N/A')} / {room.wing && typeof room.wing === 'object' ? (room.wing.name) : (room.wing || 'N/A')}
                                                </TableCell>
                                                <TableCell>{room.occupancy || `${room.maxAdults || 0}A ${room.maxKids || 0}C`}</TableCell>
                                                <TableCell>{formatCurrency(room.rate || room.baseRate || 0, currency)}</TableCell>
                                                <TableCell>
                                                    {(room.amenities || []).slice(0, 2).map((amenity, i) => (
                                                        <Chip key={i} label={amenity} size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5, fontSize: '0.7rem' }} />
                                                    ))}
                                                    {(room.amenities || []).length > 2 && (
                                                        <Tooltip title={(room.amenities || []).slice(2).join(', ')}>
                                                            <Chip label={`+${room.amenities.length - 2}`} size="small" variant="outlined" sx={{ mb: 0.5, fontSize: '0.7rem', cursor: 'pointer' }} />
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {room.status === 'OOO' || room.status === 'Out of Order' ? (
                                                        <Tooltip 
                                                            title={getOOOStaffName(room) ? `Assigned to: ${getOOOStaffName(room)}` : "Assigned to: Unassigned"} 
                                                            arrow 
                                                            placement="top"
                                                        >
                                                            <Chip 
                                                                label={room.status} 
                                                                size="small" 
                                                                color={getStatusColor(room.status)} 
                                                                sx={{ fontWeight: 'bold', cursor: 'pointer' }} 
                                                            />
                                                        </Tooltip>
                                                    ) : (
                                                        <Chip label={room.status} size="small" color={getStatusColor(room.status)} sx={{ fontWeight: 'bold' }} />
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Tooltip title="View Room Details">
                                                        <IconButton size="small" color="primary" onClick={() => navigate(`/room-management/${room.id}`)}>
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Generate QR Code">
                                                        <IconButton size="small" color="info" onClick={() => setQrModal({ open: true, data: room })}><QrCodeIcon fontSize="small" /></IconButton>
                                                    </Tooltip>
                                                    {room.status === 'OOO' || room.status === 'Out of Order' ? (
                                                        <>
                                                            <Tooltip title="Resolve Out-of-Order Early">
                                                                <IconButton size="small" color="success" onClick={() => handleResolveOOOClick(room)}><CheckCircleIcon fontSize="small" /></IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Extend Out-of-Order">
                                                                <IconButton size="small" color="warning" onClick={() => setOooModal({ open: true, data: room, isExtend: true })}><MoreTimeIcon fontSize="small" /></IconButton>
                                                            </Tooltip>
                                                        </>
                                                    ) : (
                                                        <Tooltip title="Set Out-of-Order">
                                                            <IconButton size="small" color="secondary" onClick={() => setOooModal({ open: true, data: room })}><BuildIcon fontSize="small" /></IconButton>
                                                        </Tooltip>
                                                    )}
                                                    <Tooltip title="Archive Room">
                                                        <IconButton size="small" color="warning" onClick={() => handleArchiveClick(room.id)}><ArchiveIcon fontSize="small" /></IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={isGeometryLocked ? "Structural edits are locked while property is Active" : "Edit Room"}>
                                                        <span>
                                                            <IconButton size="small" color="primary" onClick={() => setRoomModal({ open: true, data: room })} disabled={isGeometryLocked}><EditIcon fontSize="small" /></IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title={isGeometryLocked ? "Deletion is locked while property is Active" : "Delete Room"}>
                                                        <span>
                                                            <IconButton size="small" color="error" onClick={() => handleDeleteClick(room.id)} disabled={isGeometryLocked}><DeleteIcon fontSize="small" /></IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                                <Typography color="text.secondary">No rooms found.</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                            <TablePagination
                                rowsPerPageOptions={[20, 50, 100]}
                                component="div"
                                count={roomsList.length === 0 ? 0 : totalRooms}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={(e, newPage) => setPage(newPage)}
                                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                            />
                        </TableContainer>
                    </TabPanel>

                    {/* Room Types */}
                    <TabPanel value={tabValue} index={1}>
                        <RoomTypeSetup />
                    </TabPanel>

                    {/* Structure */}
                    <TabPanel value={tabValue} index={2}>
                        <Grid container spacing={4}>
                            <Grid item xs={12} md={6}>
                                <FloorSetup />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <WingSetup />
                            </Grid>
                        </Grid>
                    </TabPanel>

                    {/* Amenities */}
                    <TabPanel value={tabValue} index={3}>
                        <AmenitySetup />
                    </TabPanel>
                </Box>

            <BulkQRPrintModal
                open={bulkQrModal}
                onClose={() => setBulkQrModal(false)}
                rooms={roomsList}
                tenantId={JSON.parse(localStorage.getItem('adminAuth') || '{}').tenantId || '1'}
            />

            <RoomFormModal 
                open={roomModal.open}
                onClose={() => setRoomModal({ open: false, data: null })}
                onSave={handleSaveRoom}
                editingRoom={roomModal.data}
            />

            <ConfirmDeleteModal
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, roomId: null })}
                onConfirm={confirmDelete}
                itemName="room"
            />

            <ConfirmDeleteModal
                open={archiveDialog.open}
                onClose={() => setArchiveDialog({ open: false, roomId: null })}
                onConfirm={confirmArchive}
                title="Archive Room"
                bodyText="Are you sure you want to archive this room? This will hide it from active operations."
                confirmText="Archive"
                confirmColor="warning"
            />

            {oooModal.data && (
                <RoomOOOModal
                    open={oooModal.open}
                    onClose={() => setOooModal({ open: false, data: null, isBulk: false })}
                    room={oooModal.data}
                    isExtend={oooModal.isExtend}
                    isBulk={oooModal.isBulk}
                    onConfirm={handleOOOConfirm}
                />
            )}

            <RoomQRCodeModal
                open={qrModal.open}
                onClose={() => setQrModal({ open: false, data: null })}
                room={qrModal.data}
                tenantId={JSON.parse(localStorage.getItem('adminAuth') || '{}').tenantId || '1'}
            />

            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={() => setNotification({ ...notification, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Paper sx={{ 
                    p: 2, 
                    minWidth: 300, 
                    maxWidth: 400,
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: 2, 
                    borderRadius: 2, 
                    borderLeft: '6px solid', 
                    borderColor: notification.severity === 'error' ? 'error.main' : 'success.main',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                    bgcolor: 'background.paper'
                }}>
                    <Box sx={{ flex: 1, pt: 0.5 }}>
                        <Typography variant="subtitle2" fontWeight="bold" color={notification.severity === 'error' ? 'error.main' : 'success.main'} mb={0.5}>
                            {notification.severity === 'error' ? 'Action Failed' : 'Success'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                            {notification.message}
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setNotification({ ...notification, open: false })} sx={{ mt: -0.5, mr: -1 }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Paper>
            </Snackbar>
        </Box>
    );
}
